import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, TextField, Button, CircularProgress, Alert,
  Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Grid,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { exportsApi, ExportsFilter } from '@services/api/exports';

const COLORS = {
  bg: '#0f1419', card: '#1a2332', cardBorder: '#2a3444',
  text: '#ffffff', muted: '#8b95a5', orange: '#FF6B4A', green: '#00D084', red: '#FF4D4F',
};

const KIND_TABS: { key: NonNullable<ExportsFilter['kind']>; label: string }[] = [
  { key: 'export', label: 'Exports' },
  { key: 'roll_read', label: 'Roll reads' },
  { key: 'gap_edit', label: 'Gap edits' },
  { key: 'everything', label: 'Everything' },
];

const DAY_TABS: { key: number | 'all'; label: string }[] = [
  { key: 7, label: '7 days' }, { key: 30, label: '30 days' }, { key: 90, label: '90 days' }, { key: 'all', label: 'All time' },
];

const KPICard: React.FC<{ title: string; value: string | number; subtext?: string; color: string }> = ({ title, value, subtext, color }) => (
  <Card sx={{ backgroundColor: COLORS.card, borderLeft: `4px solid ${color}` }}>
    <CardContent sx={{ p: 2 }}>
      <Typography sx={{ fontSize: '0.75rem', color: COLORS.muted, fontWeight: 500, mb: 1, textTransform: 'uppercase', letterSpacing: 0.3 }}>{title}</Typography>
      <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'white' }}>{typeof value === 'number' ? value.toLocaleString() : value}</Typography>
      {subtext && <Typography sx={{ fontSize: '0.68rem', color: COLORS.muted, mt: 0.5 }}>{subtext}</Typography>}
    </CardContent>
  </Card>
);

export const ExportsPage: React.FC = () => {
  const [kind, setKind] = useState<NonNullable<ExportsFilter['kind']>>('export');
  const [days, setDays] = useState<number | 'all'>(30);
  const [minRows, setMinRows] = useState(0);
  const [sort, setSort] = useState<NonNullable<ExportsFilter['sort']>>('largest_first');

  const exportsQuery = useQuery({
    queryKey: ['exports', kind, days, minRows, sort],
    queryFn: () => exportsApi.getExports({ kind, days, min_rows: minRows, sort }),
    staleTime: 30000,
  });

  const data = exportsQuery.data;
  const rows = data?.rows || [];

  const largestSingle = data?.largest_single || 0;
  const largestSingleDate = rows.length > 0
    ? [...rows].sort((a, b) => b.rows - a.rows)[0]?.when
    : null;

  return (
    <Box sx={{ pb: 4, color: COLORS.text }}>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Access events" value={data?.access_events ?? 0} color="#1890FF" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="People disclosed" value={data ? `${(data.people_disclosed / 1000).toFixed(1)}K` : 0} subtext="rows returned, all masked" color="#FAAD14" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Distinct users" value={data?.distinct_users ?? 0} color="#00D084" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Largest single"
            value={largestSingle.toLocaleString()}
            subtext={largestSingleDate ? `last ${new Date(largestSingleDate).toLocaleDateString('en-KE', { day: '2-digit', month: 'short' })}` : undefined}
            color={COLORS.red}
          />
        </Grid>
      </Grid>

      <Card sx={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.cardBorder}` }}>
        <Box sx={{ p: 1.5, borderBottom: `1px solid ${COLORS.cardBorder}` }}>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 700 }}>Personal-data access</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: COLORS.muted }}>PDF downloads of a masked roll</Typography>
        </Box>

        <Box sx={{ p: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', borderBottom: `1px solid ${COLORS.cardBorder}` }}>
          {KIND_TABS.map((t) => (
            <Chip
              key={t.key} label={t.label} onClick={() => setKind(t.key)} size="small"
              sx={{
                fontWeight: kind === t.key ? 700 : 500,
                backgroundColor: kind === t.key ? 'rgba(0, 208, 132, 0.18)' : COLORS.bg,
                color: kind === t.key ? COLORS.green : COLORS.muted,
                border: `1px solid ${kind === t.key ? COLORS.green : COLORS.cardBorder}`,
              }}
            />
          ))}
          <Box sx={{ width: 12 }} />
          {DAY_TABS.map((t) => (
            <Chip
              key={t.label} label={t.label} onClick={() => setDays(t.key)} size="small"
              sx={{
                fontWeight: days === t.key ? 700 : 500,
                backgroundColor: days === t.key ? 'rgba(0, 208, 132, 0.18)' : COLORS.bg,
                color: days === t.key ? COLORS.green : COLORS.muted,
                border: `1px solid ${days === t.key ? COLORS.green : COLORS.cardBorder}`,
              }}
            />
          ))}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography sx={{ fontSize: '0.75rem', color: COLORS.muted }}>At least</Typography>
            <TextField
              size="small" type="number" value={minRows}
              onChange={(e) => setMinRows(Math.max(0, parseInt(e.target.value, 10) || 0))}
              sx={{ width: 70, '& .MuiOutlinedInput-input': { py: 0.5, fontSize: '0.8rem' }, backgroundColor: COLORS.bg }}
            />
            <Typography sx={{ fontSize: '0.75rem', color: COLORS.muted }}>rows</Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          {(['largest_first', 'newest_first'] as const).map((s) => (
            <Chip
              key={s} label={s === 'largest_first' ? 'Largest first' : 'Newest first'} onClick={() => setSort(s)} size="small"
              sx={{
                fontWeight: sort === s ? 700 : 500,
                backgroundColor: sort === s ? 'rgba(0, 208, 132, 0.18)' : COLORS.bg,
                color: sort === s ? COLORS.green : COLORS.muted,
                border: `1px solid ${sort === s ? COLORS.green : COLORS.cardBorder}`,
              }}
            />
          ))}
          <Button
            size="small" variant="outlined" startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
            onClick={() => exportsQuery.refetch()}
            sx={{ borderColor: COLORS.cardBorder, color: COLORS.muted, textTransform: 'none', '&:hover': { borderColor: COLORS.green, color: COLORS.green } }}
          >
            Refresh
          </Button>
        </Box>

        {exportsQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress sx={{ color: COLORS.green }} /></Box>
        ) : exportsQuery.error ? (
          <Alert severity="error" sx={{ m: 2 }}>Failed to load access log</Alert>
        ) : kind === 'roll_read' ? (
          <Typography sx={{ p: 3, color: COLORS.muted, textAlign: 'center' }}>
            Roll views aren't logged yet — only PDF exports and gap-figure corrections are currently tracked.
          </Typography>
        ) : rows.length === 0 ? (
          <Typography sx={{ p: 3, color: COLORS.muted, textAlign: 'center' }}>No access events for this filter</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#2a3444' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: COLORS.muted, letterSpacing: 0.4 }}>WHEN</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: COLORS.muted, letterSpacing: 0.4 }}>WHO</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: COLORS.muted, letterSpacing: 0.4 }}>KIND</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: COLORS.muted, letterSpacing: 0.4 }}>AREA</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.68rem', color: COLORS.muted, letterSpacing: 0.4 }}>ROWS</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: COLORS.muted, letterSpacing: 0.4 }}>STATED REASON</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => (
                  <TableRow key={idx} sx={{ borderBottom: `1px solid ${COLORS.cardBorder}`, '&:hover': { backgroundColor: '#252d3d' } }}>
                    <TableCell sx={{ fontSize: '0.78rem', color: COLORS.muted }}>
                      {new Date(row.when).toLocaleString('en-KE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', color: COLORS.text, fontWeight: 600 }}>
                      {row.who} {row.role && <Typography component="span" sx={{ color: COLORS.muted, fontWeight: 400 }}>· {row.role}</Typography>}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', color: COLORS.muted }}>{row.kind.replace('_', ' ')}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', color: COLORS.muted }}>{row.area || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 700, color: row.rows >= 500 ? COLORS.red : COLORS.orange }}>
                      {row.rows.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', color: COLORS.muted }}>{row.reason || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ p: 1.5 }}>
          <Typography sx={{ fontSize: '0.68rem', color: COLORS.muted }}>
            Rows highlighted in red disclosed 500 or more people in one go. Every export is masked; no unmasked ID number has ever left the database.
          </Typography>
        </Box>
      </Card>
    </Box>
  );
};
